import { cx, css } from '@mildang/styled-system/css';
import { splitCssProps } from '@mildang/styled-system/jsx';
import { token } from '@mildang/styled-system/tokens';
import {
  textColor,
  type TypographyVariantProps,
  typography,
  type TypographyVariant,
} from '@mildang/styled-system/recipes';
import type {
  ColorToken,
  JsxStyleProps,
  SystemStyleObject,
  UnstyledProps,
} from '@mildang/styled-system/types';
import type { PandaStylePropName } from '../../types/HTMLStyledProps';
import {
  type ComponentPropsWithRef,
  type CSSProperties,
  type ElementType,
  type ForwardedRef,
  forwardRef,
  createElement,
  type ReactNode,
  type Ref,
  type RefAttributes,
} from 'react';

type FixedForwardRef = <T, P = {}>(
  render: (props: P, ref: Ref<T>) => ReactNode,
) => (props: P & RefAttributes<T>) => ReactNode;

const fixedForwardRef = forwardRef as FixedForwardRef;

/** 토큰 외에 글자 색으로 받는 CSS 키워드. raw 색(`#fff`, `rgb()`)은 받지 않는다. */
type TextColorKeyword = 'inherit' | 'currentColor' | 'transparent';

/** `Text` 의 문자열 `color`. 존재하는 color 토큰과 CSS 키워드만 받아 오타·없는 토큰을 타입에서 막는다. */
export type TextColor = ColorToken | TextColorKeyword;

/** 반응형·조건 객체 `color`(`{ base, md }`). 이름을 붙여 둬야 문서 metadata가 v2 조건 타입을 펼치지 않는다. */
export type TextColorCondition = Exclude<SystemStyleObject['color'], string>;

type DistributiveOmit<T, TOmitted extends PropertyKey> = T extends any ? Omit<T, TOmitted> : never;

/**
 * DOM props 는 `ComponentPropsWithRef` 항 **하나로만** 받는다. `as` 를 안 넘긴 `<Text>` 는 `p` 로
 * 폴백한다.
 *
 * React 쪽에서 panda style prop 과 이름이 같은 속성은 전부 지운다 — `splitCssProps` 가 그 이름을
 * 무조건 style 로 가르므로 DOM 에 닿지 않는다(사유는 `PandaStylePropName` 주석).
 *
 * ⚠️ **panda 쪽 항으로 `PandaHTMLStyledProps<TAs>` 를 쓰지 않는다** — 제네릭 `TAs` 는 `ElementType`
 * 거대 union 으로 인스턴스화되고, 그 별칭의 비분배 `Omit` 이 union 을 `{}` 로 붕괴시킨다(실측).
 * 분배형으로 바꾸면 `Assign` 에서 TS2590. 그래서 여기서는 panda 항만 직접 교차한다.
 */
export type TypographyProps<TAs extends ElementType> = TypographyVariantProps &
  DistributiveOmit<ComponentPropsWithRef<ElementType extends TAs ? 'p' : TAs>, 'as' | PandaStylePropName> & {
    as?: TAs;
  } & UnstyledProps &
  Omit<JsxStyleProps, 'css' | 'color'> & {
    /**
     * 글자 색. color 토큰(`neutral.text.low`)이나 CSS 키워드(`currentColor`, `inherit`)를 받는다.
     * 반응형·조건 객체는 Panda 경로라 모노레포 안에서만 받는다(배포본에서는 `SystemStyleObject` 가 닫혀 있다).
     */
    // 문서 Props 표에 키워드가 보이도록 `TextColor` 별칭 대신 값을 풀어 적는다(`TextColor` 와 같은 값).
    color?:
      | ColorToken
      | 'inherit'
      | 'currentColor'
      | 'transparent'
      | TextColorCondition;
    sx?: SystemStyleObject | SystemStyleObject[];
  };

/**
 * 문자열 `color` 는 Panda style prop 으로 넘기지 않고 CSS 변수 한 겹을 거친다.
 * style prop 은 값이 소스에 리터럴로 있어야 규칙이 생겨서, 변수로 넘긴 값과 외부 배포본(style
 * props 가 닫혀 있다)에서는 색이 조용히 빠진다. 규칙은 recipe(`textColor`) 하나로 고정하고 값만
 * 인라인 custom property 로 넘긴다(`iconColorStyle.ts` 와 같은 방식).
 *
 * 반응형·조건 객체(`{ base, md }`)는 CSS 변수로 표현할 수 없어 지금처럼 Panda `css()` 로 넘긴다.
 * 정적 추출에 기대므로 모노레포 내부에서만 동작한다.
 */
function textColorCustomProperty(color: string): CSSProperties {
  const resolved = token.var(`colors.${color}` as Parameters<typeof token.var>[0]) ?? color;
  return { '--ids-text-color': resolved } as CSSProperties;
}

function TextBase<TAs extends ElementType>(props: TypographyProps<TAs>, ref: ForwardedRef<any>) {
  // `splitCssProps` 가 style prop 으로 가져가기 전에 꺼낸다.
  const { color, ...propsWithoutColor } = props;
  const stringColor = typeof color === 'string' ? color : undefined;
  const [cssProps, restProps] = splitCssProps(propsWithoutColor);
  const { css: _cssProp, ...styleProps } = cssProps;
  if (color != null && stringColor === undefined) {
    (styleProps as SystemStyleObject).color = color;
  }
  const { sx, ref: _ref, ...restPropsWithoutSx } = restProps;
  const [variantProps, localProps] = typography.splitVariantProps(restPropsWithoutSx);

  const {
    as: AsComponent = 'p',
    className: classNameProp,
    style: styleProp,
    ...others
  } = localProps as unknown as Omit<TypographyProps<TAs>, keyof TypographyVariant | 'ref'>;

  const Component = AsComponent as ElementType;

  const className = css(styleProps, sx);

  return createElement(Component, {
    ref,
    className: cx(typography(variantProps), stringColor && textColor(), className, classNameProp),
    style: stringColor ? { ...textColorCustomProperty(stringColor), ...styleProp } : styleProp,
    ...others,
  });
}
/**
 * 타입을 **명시적으로** 붙인다. `fixedForwardRef(TextBase)` 를 그대로 내보내면 선언 방출기가
 * 제네릭 `P` 를 추론하지 못해 `.d.ts` 에 `(props: any)` 로 찍히고, **배포 패키지를 쓰는 쪽에서만**
 * 자동완성과 타입 검사가 통째로 사라진다(소스로 쓰는 모노레포 안에서는 안 보이는 결함).
 */
export const Text: <TAs extends ElementType = 'p'>(props: TypographyProps<TAs>) => ReactNode =
  fixedForwardRef(TextBase);

export default Text;
